import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { GAME_FONT_FAMILY, TILE_SIZE } from '@shared/constants';
import {
  getBodySpriteKey,
  getBodyCenterOffset,
  getBodyAnimKey,
  NPC_BODY_IDS,
} from '../../sprites/BodySpriteManager';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

type BodyGalleryPanelCallbacks = {
  onMove(x: number, y: number): void;
  getLocalPlayerHeadId(): number;
  /**
   * Se invoca cuando el usuario aplica un body al personaje (click en lista).
   * bodyId = 0 limpia el override y restaura el body por equipamiento.
   */
  onApplyBody(bodyId: number): void;
  /** Se invoca al togglear "ocultar head" (para vestir bodies de NPC sin head). */
  onToggleHeadHidden(hidden: boolean): void;
};

type BodyGalleryPanelOptions = {
  initialX: number;
  initialY: number;
  BODY_FRAME_HEIGHT: number;
  HEAD_OFFSET_FROM_BODY_BOTTOM: number;
  HEAD_FRAME_HEIGHT: number;
};

/**
 * Configuración de cada celda del grid 2x2 de preview.
 * Cada celda muestra el body+head animado caminando en una dirección fija,
 * con líneas verde fina vertical y horizontal cruzando el centro para validar centrado.
 */
type PreviewCell = {
  direction: Direction;
  cx: number; // centro X de la celda
  cy: number; // centro Y de la celda
  cellW: number;
  cellH: number;
  bodyBottomY: number;
  box: Phaser.GameObjects.Rectangle;
  body: Phaser.GameObjects.Sprite;
  head: Phaser.GameObjects.Sprite;
  vLine: Phaser.GameObjects.Rectangle;
  hLine: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
};

type BodyGalleryPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  listBg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  list: Phaser.GameObjects.Text[];
  previewCells: PreviewCell[];
  closeButton: Phaser.GameObjects.Text;
  restoreButton: Phaser.GameObjects.Text;
  headToggleButton: Phaser.GameObjects.Text;
  scrollUp: Phaser.GameObjects.Text;
  scrollDown: Phaser.GameObjects.Text;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  colPadding: number;
  listColWidth: number;
  previewColWidth: number;
  previewStartX: number;
  previewCenterX: number;
  rowHeight: number;
  listStartY: number;
};

// Orden fijo del grid 2x2:
//   [UP]   [DOWN]
//   [LEFT] [RIGHT]
const CELL_DIRECTIONS: Direction[] = [Direction.UP, Direction.DOWN, Direction.LEFT, Direction.RIGHT];

const CELL_DIR_LABEL: Record<Direction, string> = {
  [Direction.UP]: 'ARRIBA',
  [Direction.DOWN]: 'ABAJO',
  [Direction.LEFT]: 'IZQ',
  [Direction.RIGHT]: 'DER',
};

export class BodyGalleryPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: BodyGalleryPanelCallbacks;
  private readonly constants: {
    BODY_FRAME_HEIGHT: number;
    HEAD_OFFSET_FROM_BODY_BOTTOM: number;
    HEAD_FRAME_HEIGHT: number;
  };
  private panel: BodyGalleryPanelElements | null = null;
  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  // Candidatos: bodies de player (1-112) + bodies de NPC (criaturas, 201+). La lista real
  // (availableBodyIds) se filtra a los que tienen textura cargada (ver getAvailableBodyIds).
  private static readonly CANDIDATE_BODY_IDS: number[] = [
    ...Array.from({ length: 112 }, (_, i) => i + 1),
    ...NPC_BODY_IDS,
  ];
  private availableBodyIds: number[] = [];
  private selectedId: number = 1;
  private scrollOffset: number = 0;
  private headHidden: boolean = false;

  constructor(
    scene: Phaser.Scene,
    callbacks: BodyGalleryPanelCallbacks,
    options: BodyGalleryPanelOptions
  ) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.constants = {
      BODY_FRAME_HEIGHT: options.BODY_FRAME_HEIGHT,
      HEAD_OFFSET_FROM_BODY_BOTTOM: options.HEAD_OFFSET_FROM_BODY_BOTTOM,
      HEAD_FRAME_HEIGHT: options.HEAD_FRAME_HEIGHT,
    };
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.setVisible(this.isOpen);
  }

  open(): void {
    this.isOpen = true;
    this.setVisible(true);
  }

  close(): void {
    this.isOpen = false;
    this.setVisible(false);
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const panelWidth = 520;
    const panelHeight = 320;
    const panelX = initialX;
    const panelY = initialY;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const colPadding = 12;
    const listColWidth = panelWidth * 0.45;
    const previewColWidth = panelWidth * 0.55 - colPadding * 3;

    const baseX = panelX - panelWidth / 2;
    const baseY = panelY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const previewStartX = leftStartX + listColWidth + colPadding;

    const listBg = this.scene.add
      .rectangle(
        leftStartX + listColWidth / 2,
        panelY,
        listColWidth,
        panelHeight - colPadding * 2,
        0x222222,
        0.8
      )
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x555555)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const title = this.scene.add
      .text(panelX, baseY + 16, 'Galería de bodies', withGameFont({
        fontSize: '16px',
        color: '#ffffff',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = pointer.x - bg.x;
      this.dragOffsetY = pointer.y - bg.y;
      pointer.event.stopPropagation();
    });

    const scrollUp = this.scene.add
      .text(leftStartX + listColWidth - 8, baseY + 10, '▲', withGameFont({
        fontSize: '12px',
        color: '#ffffaa',
      }))
      .setOrigin(1, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    scrollUp.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.scrollOffset = Math.max(0, this.scrollOffset - (this.panel?.list.length ?? 14));
      this.refreshContents();
      pointer.event.stopPropagation();
    });

    const scrollDown = this.scene.add
      .text(leftStartX + listColWidth - 8, baseY + panelHeight - 26, '▼', withGameFont({
        fontSize: '12px',
        color: '#ffffaa',
      }))
      .setOrigin(1, 0)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    scrollDown.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const maxOffset = Math.max(0, this.availableBodyIds.length - (this.panel?.list.length ?? 14));
      this.scrollOffset = Math.min(maxOffset, this.scrollOffset + (this.panel?.list.length ?? 14));
      this.refreshContents();
      pointer.event.stopPropagation();
    });

    const listStartY = baseY + 36;
    const rowHeight = 18;
    const list: Phaser.GameObjects.Text[] = [];
    const visibleRows = 14;

    for (let i = 0; i < visibleRows; i += 1) {
      const y = listStartY + i * rowHeight;
      const txt = this.scene.add
        .text(leftStartX + 6, y, '', withGameFont({
          fontSize: '12px',
          color: '#ffffff',
        }))
        .setOrigin(0, 0)
        .setDepth(1101)
        .setScrollFactor(0)
        .setVisible(false)
        .setInteractive({ useHandCursor: true });

      const index = i;
      txt.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        this.handleListClick(index);
        pointer.event.stopPropagation();
      });

      list.push(txt);
    }

    const previewCenterX = previewStartX + previewColWidth / 2;

    // Crear las 4 celdas del grid 2x2
    const cellLayout = this.computeCellLayout(previewStartX, baseY, previewColWidth, panelHeight);
    const previewCells: PreviewCell[] = CELL_DIRECTIONS.map((dir, idx) => {
      const { cx, cy, cellW, cellH, bodyBottomY } = cellLayout[idx];
      return this.createPreviewCell(dir, cx, cy, cellW, cellH, bodyBottomY);
    });

    const closeButton = this.scene.add
      .text(panelX + panelWidth / 2 - 16, baseY + 8, '✕', withGameFont({
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#555555',
      }))
      .setOrigin(1, 0)
      .setPadding(4, 2, 4, 2)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    const restoreButton = this.scene.add
      .text(previewCenterX - 58, baseY + panelHeight - 18, '↺ Restaurar', withGameFont({
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: '#444444',
      }))
      .setOrigin(0.5, 0.5)
      .setPadding(6, 4, 6, 4)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    restoreButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.callbacks.onApplyBody(0);
      pointer.event.stopPropagation();
    });

    // Toggle ocultar/mostrar head (para bodies de NPC que ya traen cabeza).
    const headToggleButton = this.scene.add
      .text(previewCenterX + 58, baseY + panelHeight - 18, this.headToggleLabel(), withGameFont({
        fontSize: '12px',
        color: '#ffffff',
        backgroundColor: this.headHidden ? '#774444' : '#446644',
      }))
      .setOrigin(0.5, 0.5)
      .setPadding(6, 4, 6, 4)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false)
      .setInteractive({ useHandCursor: true });

    headToggleButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.headHidden = !this.headHidden;
      this.refreshHeadToggleButton();
      this.callbacks.onToggleHeadHidden(this.headHidden);
      this.updatePreview(this.selectedId);
      pointer.event.stopPropagation();
    });

    this.panel = {
      bg,
      listBg,
      title,
      list,
      previewCells,
      closeButton,
      restoreButton,
      headToggleButton,
      scrollUp,
      scrollDown,
      panelX,
      panelY,
      panelWidth,
      panelHeight,
      colPadding,
      listColWidth,
      previewColWidth,
      previewStartX,
      previewCenterX,
      rowHeight,
      listStartY,
    };
  }

  /**
   * Calcula posición y tamaño de cada celda del grid 2x2 dentro del área de preview.
   * Orden devuelto se corresponde con CELL_DIRECTIONS: [UP, DOWN, LEFT, RIGHT].
   */
  private computeCellLayout(
    previewStartX: number,
    baseY: number,
    previewColWidth: number,
    panelHeight: number
  ): Array<{ cx: number; cy: number; cellW: number; cellH: number; bodyBottomY: number }> {
    const gridTopY = baseY + 36; // bajo el título
    const gridBottomY = baseY + panelHeight - 36; // arriba del botón Restaurar
    const gridHeight = gridBottomY - gridTopY;
    const cellGap = 6;
    const cellW = (previewColWidth - cellGap) / 2;
    const cellH = (gridHeight - cellGap) / 2;

    const leftColX = previewStartX + cellW / 2;
    const rightColX = leftColX + cellW + cellGap;
    const topRowY = gridTopY + cellH / 2;
    const bottomRowY = topRowY + cellH + cellGap;
    // El sprite (origin 0.5, 1) se ancla 4px arriba del borde inferior de la celda.
    const topBodyBottomY = topRowY + cellH / 2 - 4;
    const bottomBodyBottomY = bottomRowY + cellH / 2 - 4;

    return [
      { cx: leftColX, cy: topRowY, cellW, cellH, bodyBottomY: topBodyBottomY }, // UP
      { cx: rightColX, cy: topRowY, cellW, cellH, bodyBottomY: topBodyBottomY }, // DOWN
      { cx: leftColX, cy: bottomRowY, cellW, cellH, bodyBottomY: bottomBodyBottomY }, // LEFT
      { cx: rightColX, cy: bottomRowY, cellW, cellH, bodyBottomY: bottomBodyBottomY }, // RIGHT
    ];
  }

  private createPreviewCell(
    direction: Direction,
    cx: number,
    cy: number,
    cellW: number,
    cellH: number,
    bodyBottomY: number
  ): PreviewCell {
    const headCenterY =
      bodyBottomY - this.constants.HEAD_OFFSET_FROM_BODY_BOTTOM - this.constants.HEAD_FRAME_HEIGHT / 2;
    // Centro del tile donde "está parado" el body (tile 32x32 con base en bodyBottomY).
    const tileCenterX = cx;
    const tileCenterY = bodyBottomY - TILE_SIZE / 2;

    const box = this.scene.add
      .rectangle(cx, cy, cellW, cellH, 0x000000, 0.8)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0xffffff)
      .setDepth(1100)
      .setScrollFactor(0)
      .setVisible(false);

    const body = this.scene.add
      .sprite(cx, bodyBottomY, getBodySpriteKey(this.selectedId))
      .setOrigin(0.5, 1)
      .setVisible(false)
      .setDepth(1100)
      .setScrollFactor(0);

    const head = this.scene.add
      .sprite(cx, headCenterY, getHeadSpriteKey(this.callbacks.getLocalPlayerHeadId()))
      .setOrigin(0.5, 0.5)
      .setVisible(false)
      .setDepth(1100)
      .setScrollFactor(0);

    // Línea vertical verde fina (eje X del tile donde se para el body)
    const vLine = this.scene.add
      .rectangle(tileCenterX, cy, 0.5, cellH, 0x00ff00, 0.8)
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    // Línea horizontal verde fina (eje Y del tile donde se para el body)
    const hLine = this.scene.add
      .rectangle(cx, tileCenterY, cellW, 0.5, 0x00ff00, 0.8)
      .setOrigin(0.5, 0.5)
      .setDepth(1101)
      .setScrollFactor(0)
      .setVisible(false);

    const label = this.scene.add
      .text(cx, cy - cellH / 2 + 8, CELL_DIR_LABEL[direction], withGameFont({
        fontSize: '10px',
        color: '#aaaaaa',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(1102)
      .setScrollFactor(0)
      .setVisible(false);

    return {
      direction,
      cx,
      cy,
      cellW,
      cellH,
      bodyBottomY,
      box,
      body,
      head,
      vLine,
      hLine,
      label,
    };
  }

  /** Bodies candidatos que REALMENTE tienen textura cargada (sheet válido, no __MISSING). */
  private getAvailableBodyIds(): number[] {
    return BodyGalleryPanel.CANDIDATE_BODY_IDS.filter((id) => {
      const key = getBodySpriteKey(id);
      if (!this.scene.textures.exists(key)) return false;
      return this.scene.textures.get(key).key !== '__MISSING';
    });
  }

  private headToggleLabel(): string {
    return this.headHidden ? 'Head: oculta' : 'Head: visible';
  }

  private refreshHeadToggleButton(): void {
    const btn = this.panel?.headToggleButton;
    if (!btn) return;
    btn.setText(this.headToggleLabel());
    btn.setBackgroundColor(this.headHidden ? '#774444' : '#446644');
  }

  private handleListClick(index: number): void {
    const globalIndex = this.scrollOffset + index;
    const bodyId = this.availableBodyIds[globalIndex];
    if (!bodyId) return;
    this.selectedId = bodyId;
    this.refreshContents();
    // Aplicar al personaje (server-side) para admins/debug.
    this.callbacks.onApplyBody(bodyId);
  }

  private refreshContents(): void {
    if (!this.panel) return;

    // Recalcular la lista de bodies disponibles (los que tienen textura cargada).
    this.availableBodyIds = this.getAvailableBodyIds();

    const rows = this.panel.list.length;
    const maxOffset = Math.max(0, this.availableBodyIds.length - rows);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, 0, maxOffset);

    for (let i = 0; i < this.panel.list.length; i += 1) {
      const txt = this.panel.list[i];
      const bodyId = this.availableBodyIds[this.scrollOffset + i];
      if (bodyId) {
        txt.setText(bodyId >= 200 ? `Body ${bodyId} (NPC)` : `Body ${bodyId}`);
        if (bodyId === this.selectedId) {
          txt.setColor('#ffff00');
        } else {
          txt.setColor('#ffffff');
        }
      } else {
        txt.setText('');
      }
    }

    if (!this.availableBodyIds.includes(this.selectedId)) {
      this.selectedId = this.availableBodyIds[0] ?? 1;
    }

    this.updatePreview(this.selectedId);
  }

  /**
   * Aplica el bodyId seleccionado a las 4 celdas, cada una con su propia dirección
   * y animación de caminar continua.
   */
  private updatePreview(bodyId: number): void {
    if (!this.panel) return;
    this.selectedId = bodyId;

    const bodyOffsetX = getBodyCenterOffset(bodyId);
    const headId = this.callbacks.getLocalPlayerHeadId();
    const bodyTextureKey = getBodySpriteKey(bodyId);

    for (const cell of this.panel.previewCells) {
      const bodyAnimKey = getBodyAnimKey(bodyId, cell.direction);
      const headCenterY =
        cell.bodyBottomY - this.constants.HEAD_OFFSET_FROM_BODY_BOTTOM - this.constants.HEAD_FRAME_HEIGHT / 2;

      cell.body
        .setOrigin(0.5, 1)
        .setTexture(bodyTextureKey)
        .setPosition(cell.cx + bodyOffsetX, cell.bodyBottomY)
        .setVisible(true);

      if (cell.body.anims.currentAnim?.key !== bodyAnimKey || !cell.body.anims.isPlaying) {
        cell.body.anims.play(bodyAnimKey, true);
      }

      if (headId > 0 && !this.headHidden) {
        cell.head
          .setOrigin(0.5, 0.5)
          .setTexture(getHeadSpriteKey(headId))
          .setFrame(getHeadFrameForDirection(cell.direction))
          .setPosition(cell.cx, headCenterY)
          .setVisible(true);
      } else {
        cell.head.setVisible(false);
      }
    }
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.listBg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.list.forEach((txt) => txt.setVisible(visible).setActive(visible));
    for (const cell of panel.previewCells) {
      cell.box.setVisible(visible).setActive(visible);
      cell.body.setVisible(visible).setActive(visible);
      cell.head.setVisible(visible).setActive(visible);
      cell.vLine.setVisible(visible).setActive(visible);
      cell.hLine.setVisible(visible).setActive(visible);
      cell.label.setVisible(visible).setActive(visible);
    }
    panel.closeButton.setVisible(visible).setActive(visible);
    panel.restoreButton.setVisible(visible).setActive(visible);
    panel.headToggleButton.setVisible(visible).setActive(visible);
    panel.scrollUp.setVisible(visible).setActive(visible);
    panel.scrollDown.setVisible(visible).setActive(visible);
    if (visible) {
      this.refreshContents();
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight, colPadding, listColWidth, previewColWidth, rowHeight } = panel;
    const baseX = newX - panelWidth / 2;
    const baseY = newY - panelHeight / 2;
    const leftStartX = baseX + colPadding;
    const previewStartX = leftStartX + listColWidth + colPadding;
    const previewCenterX = previewStartX + previewColWidth / 2;
    const listStartY = baseY + 36;

    panel.bg.setPosition(newX, newY);
    panel.listBg.setPosition(leftStartX + listColWidth / 2, newY);
    panel.title.setPosition(newX, baseY + 16);
    panel.scrollUp.setPosition(leftStartX + listColWidth - 8, baseY + 10);
    panel.scrollDown.setPosition(leftStartX + listColWidth - 8, baseY + panelHeight - 26);

    panel.list.forEach((txt, i) => {
      txt.setPosition(leftStartX + 6, listStartY + i * rowHeight);
    });

    // Recolocar las 4 celdas
    const cellLayout = this.computeCellLayout(previewStartX, baseY, previewColWidth, panelHeight);
    panel.previewCells.forEach((cell, idx) => {
      const { cx, cy, cellW, cellH, bodyBottomY } = cellLayout[idx];
      cell.cx = cx;
      cell.cy = cy;
      cell.cellW = cellW;
      cell.cellH = cellH;
      cell.bodyBottomY = bodyBottomY;

      const tileCenterY = bodyBottomY - TILE_SIZE / 2;
      cell.box.setPosition(cx, cy).setSize(cellW, cellH);
      cell.vLine.setPosition(cx, cy).setSize(0.5, cellH);
      cell.hLine.setPosition(cx, tileCenterY).setSize(cellW, 0.5);
      cell.label.setPosition(cx, cy - cellH / 2 + 8);
    });

    panel.closeButton.setPosition(newX + panelWidth / 2 - 16, baseY + 8);
    panel.restoreButton.setPosition(previewCenterX - 58, baseY + panelHeight - 18);
    panel.headToggleButton.setPosition(previewCenterX + 58, baseY + panelHeight - 18);

    panel.previewStartX = previewStartX;
    panel.previewCenterX = previewCenterX;

    this.updatePreview(this.selectedId);
  }
}
